
import { Vue, Component, Watch } from 'vue-property-decorator'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import Project from '@/types/project'
import axios from 'axios'
import ColumnOptions from '@/components/listview/ColumnOptions.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import SaveButton from '@/components/base/SaveButton.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SaveButton, PageHeader, ColumnOptions, ActiveFilters }
})

export default class Projects extends Vue {
  settings = {
    routeNames: { create: 'projectCreate', show: 'projectShow', edit: 'projectEdit' },
    routeParamKey: 'projectId',
    permissionNames: { show: 'projects.view', edit: 'projects.edit', delete: 'projects.delete' },
    functionNames: { getList: 'getProjectList' },
    getterNames: { list: 'projectList', loading: 'loadingProject' },
    localStorageName: 'projectTableSettings'
  }

  sortOrder = { column: 'name', order: 'ascend' }
  conditions = {
    phase: [5]
  }

  downloading = false

  localSettings = ls.get(this.settings.localStorageName)
  tableSettings:TableSettings = TableSettings.from(this.localSettings, this.conditions, this.sortOrder)

  columns:Column[] = [
    {
      title: 'Naam',
      key: 'name',
      dataIndex: 'name',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Referentie',
      key: 'reference',
      dataIndex: 'reference',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Afk',
      key: 'short_name',
      dataIndex: 'short_name',
      scopedSlots: { customRender: 'short_name' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Plaats',
      key: 'city',
      dataIndex: 'city',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Entiteit',
      key: 'entity.id',
      dataIndex: 'entity.name',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Start aankomend',
      key: 'start_date',
      scopedSlots: { customRender: 'start_date' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Start verkoop',
      key: 'start_sale_date',
      scopedSlots: { customRender: 'start_sale_date' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Start bouw',
      key: 'start_construction_date',
      scopedSlots: { customRender: 'start_construction_date' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Einde',
      key: 'end_date',
      scopedSlots: { customRender: 'end_date' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Fase',
      key: 'phase',
      dataIndex: 'phase_label',
      filters: [
        {
          text: 'Aankomend',
          value: 1
        },
        {
          text: 'In verkoop',
          value: 2
        },
        {
          text: 'In verhuur',
          value: 3
        },
        {
          text: 'In uitvoering',
          value: 4
        },
        {
          text: 'Opgeleverd',
          value: 5
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  filtersLoaded = false

  mounted () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    this.getFilters()
    this.onTableSettingsChange()
  }

  async getFilters () : Promise<void> {
    await this.getEntityFilter()
    this.filtersLoaded = true
  }

  getEntityFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/entity/projects', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns.filter(col => col.key === 'entity.id')[0].filters = res.data
    })
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}, archived:boolean, unrelated:boolean) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = filters
    this.tableSettings.archived = archived
    this.tableSettings.unrelated = unrelated
    this.reloadList()
  }

  reloadList () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  toggleInactiveItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.archived = event.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  toggleUnrelatedItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.unrelated = event.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  get activeColumns (): Column[] {
    return this.columns.filter(col => !this.$store.getters.InactiveProjectColumns.includes(col.key))
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          if (can('projects.edit')) {
            const route = this.$router.resolve({ name: this.settings.routeNames.edit, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          } else if (can('projects.view')) {
            const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          }
        }
      }
    }
  }

  exportExcel (includeBuildings: boolean) : void {
    this.downloading = true
    const payload = {
      selectedIds: this.itemList.data.map((item:Project) => item.id),
      withBuildings: includeBuildings,
      sorting: { }
    }

    // If the table is not sorted, we don't want to send the sorting property
    if (this.tableSettings.sort?.columnKey) {
      payload.sorting = {
        column: this.tableSettings.sort?.columnKey,
        order: this.tableSettings.sort.order
      }
    }

    axios.post(process.env.VUE_APP_API_URL + '/projects/export-excel', payload, { responseType: 'arraybuffer' }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', includeBuildings ? 'projecten_met_bouwnummers.xlsx' : 'projecten.xlsx')
      document.body.appendChild(link)
      link.click()
    }).finally(() => {
      this.downloading = false
    })
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  get itemList () : { data: Project[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Project ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
